import { useQuery } from "@tanstack/react-query";
import {
  type LucideIcon,
  Bot,
  Camera,
  Globe,
  KeyRound,
  Mail,
  MessageCircle,
  Search,
  ServerCog,
} from "lucide-react";
import { api, type Category, type IntegrationView } from "../../lib/api.js";
import { IntegrationCard } from "../../components/IntegrationCard.js";
import { SkeletonCard } from "../../components/ui/Skeleton.js";

const CATEGORY_ORDER: Category[] = ["llm", "search", "scraping", "media", "email", "messaging", "social", "cms", "infra"];

const CATEGORY_META: Record<Category, { label: string; icon: LucideIcon; description: string }> = {
  llm:         { label: "AI / LLM",       icon: Bot,        description: "Claude for production, briefs, classifiers, and the in-platform assistant." },
  search:      { label: "Search & SERP",  icon: Search,     description: "SERP snapshots, keyword volume, intent, and ranking signals." },
  scraping:    { label: "Scraping",       icon: Globe,      description: "Pulls competitor articles into structured markdown." },
  media:       { label: "Media",          icon: Camera,     description: "Image + video generation for hero, in-content, social, and short clips." },
  email:       { label: "Email",          icon: Mail,       description: "Brevo transactional + campaigns for the article-announcement send." },
  messaging:   { label: "Messaging",      icon: MessageCircle, description: "Telegram (Bot API), WhatsApp Cloud (planned)." },
  social:      { label: "Social",         icon: MessageCircle, description: "Meta (FB+IG), X, YouTube, TikTok — Phase 2." },
  cms:         { label: "CMS",            icon: ServerCog,  description: "Sanity content delivery." },
  infra:       { label: "Infrastructure", icon: ServerCog,  description: "AWS, secrets, queues." },
};

export function CredentialsPage() {
  const { data, isPending, error } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.listIntegrations(),
  });

  if (isPending) {
    return <CredentialsSkeleton />;
  }
  if (error) {
    return (
      <div className="card p-6 text-sm text-danger">
        <strong>Failed to load:</strong> {(error as Error).message}
        <p className="mt-2 text-xs text-muted">
          Is the API server running? Start it with{" "}
          <code className="rounded bg-line px-1 py-0.5 font-mono">npm run ui-server:dev</code>.
        </p>
      </div>
    );
  }

  // Group by category
  const groups = new Map<Category, IntegrationView[]>();
  for (const integ of data.integrations) {
    const arr = groups.get(integ.category) ?? [];
    arr.push(integ);
    groups.set(integ.category, arr);
  }

  const configuredCount = data.integrations.filter((i) => i.configured).length;
  const totalCount = data.integrations.length;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-ink">Integrations</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Manage every API key the AlternaBe pipeline needs. Changes write to <code className="font-mono text-xs">.env</code> and are picked up by the next CLI / MCP call — no restart needed.
          </p>
        </div>

        <div className="card flex items-center gap-3 px-4 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
            <KeyRound className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <div className="text-2xs uppercase tracking-wider text-muted">Configured</div>
            <div className="text-xl font-semibold leading-none text-ink">
              {configuredCount}<span className="font-normal text-muted">/{totalCount}</span>
            </div>
          </div>
        </div>
      </div>

      {CATEGORY_ORDER.map((cat) => {
        const group = groups.get(cat);
        if (!group || group.length === 0) return null;
        const meta = CATEGORY_META[cat];
        return (
          <section key={cat}>
            <header className="mb-3 flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-line-soft text-muted-dark">
                <meta.icon className="h-3.5 w-3.5" strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-sm font-semibold text-ink">{meta.label}</div>
                <div className="text-2xs text-muted">{meta.description}</div>
              </div>
            </header>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {group.map((integ) => (
                <IntegrationCard key={integ.id} integration={integ} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function CredentialsSkeleton() {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  );
}
